import { useEffect, useState } from 'react';
import { Plus, Trash2, Edit2, Check, X, RefreshCw, Pencil } from 'lucide-react';
import { nanoid } from 'nanoid';
import { useVault } from '../../../core/lib/vault';
import {
  listDecryptedCustomers,
  upsertCustomer,
  deleteCustomer,
  subscribe,
  type DecryptedCustomer,
} from '../sync';
import { api } from '../lib/api';
import { getAppIcon, isAppIconKey } from '../lib/appIcons';
import { ResizableAside } from './ResizableAside';
import { useCanEdit } from '../lib/usePermissions';

/**
 * Keep the regenerate cycle short so the user can "eventually get back" to
 * an icon they liked if they regenerated past it. FIFO sliding window.
 */
const EXCLUSION_WINDOW = 5;

const PRESET_COLORS = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#06b6d4',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#94a3b8',
];

export function CustomerPane({
  selectedId,
  onSelect,
  compact = false,
}: {
  selectedId: string | null;
  onSelect: (id: string, name?: string) => void;
  compact?: boolean;
}) {
  const vaultKey = useVault((s) => s.vaultKey);
  // Viewers see the customer list but can't create, rename, or delete. The
  // server enforces this too (editGate on POST/PUT/DELETE) — hiding the
  // controls keeps the UI from offering actions that would 403.
  const canEdit = useCanEdit();
  const [customers, setCustomers] = useState<DecryptedCustomer[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftColor, setDraftColor] = useState<string>(PRESET_COLORS[0]!);
  const [draftIcon, setDraftIcon] = useState<string | undefined>(undefined);
  // Sliding FIFO of recently-suggested icons we don't want to repeat. Newest
  // first; trimmed to EXCLUSION_WINDOW on every push.
  const [exclusionHistory, setExclusionHistory] = useState<string[]>([]);
  // User has either typed into the override field or regenerated — treat as
  // manual so the save-time auto-suggest doesn't fight it.
  const [iconManuallyPicked, setIconManuallyPicked] = useState(false);
  // Name at edit-start so we can tell whether a save should re-suggest.
  const [editBaselineName, setEditBaselineName] = useState('');
  // When true, show the power-user "type a lucide key" input.
  const [showKeyInput, setShowKeyInput] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  // Disable the regenerate button while a suggest call is in flight.
  const [regenerating, setRegenerating] = useState(false);

  async function reload() {
    if (!vaultKey) return;
    setCustomers(await listDecryptedCustomers(vaultKey));
  }

  useEffect(() => {
    reload();
    return subscribe(reload);
  }, [vaultKey]);

  function pushExclusion(icon: string | undefined) {
    if (!icon) return;
    setExclusionHistory((prev) => [icon, ...prev.filter((k) => k !== icon)].slice(0, EXCLUSION_WINDOW));
  }

  function resetEditSessionState() {
    setPickerResets();
  }

  function setPickerResets() {
    setExclusionHistory([]);
    setIconManuallyPicked(false);
    setShowKeyInput(false);
    setKeyInput('');
    setRegenerating(false);
  }

  function beginEdit(c: DecryptedCustomer) {
    setEditing(c.id);
    setDraftName(c.name);
    setDraftColor(c.color);
    setDraftIcon(c.icon);
    setEditBaselineName(c.name);
    resetEditSessionState();
  }

  async function addNew() {
    if (!vaultKey) return;
    const id = nanoid();
    const name = 'New Customer';
    await upsertCustomer(id, { name, color: PRESET_COLORS[0]! }, vaultKey, true);
    setEditing(id);
    setDraftName(name);
    setDraftColor(PRESET_COLORS[0]!);
    setDraftIcon(undefined);
    setEditBaselineName(name);
    resetEditSessionState();
  }

  async function regenerateIcon() {
    if (!vaultKey || regenerating) return;
    const name = draftName.trim();
    if (!name) return;
    setRegenerating(true);
    try {
      // Exclude the currently-shown icon (if any) plus the last N suggestions
      // so the model picks something genuinely new.
      const exclude = draftIcon
        ? [draftIcon, ...exclusionHistory.filter((k) => k !== draftIcon)].slice(0, EXCLUSION_WINDOW)
        : exclusionHistory;
      const res = await api.suggestIcon({ name, kind: 'customer', excludeIcons: exclude });
      if (res.icon) {
        pushExclusion(draftIcon);
        setDraftIcon(res.icon);
        // A regenerate is still "auto" — the user is asking the model for
        // ideas. We block save-time suggest only when the user has typed an
        // explicit key.
      }
    } catch {
      /* swallow */
    } finally {
      setRegenerating(false);
    }
  }

  function applyKeyInput() {
    const candidate = keyInput.trim().toLowerCase();
    if (isAppIconKey(candidate)) {
      setDraftIcon(candidate);
      setIconManuallyPicked(true);
      setShowKeyInput(false);
      setKeyInput('');
    } else {
      // Leave the input open with the bad value so the user can correct it.
    }
  }

  async function saveEdit(c: DecryptedCustomer) {
    if (!vaultKey) return;
    const nextName = draftName.trim() || c.name;
    await upsertCustomer(
      c.id,
      {
        name: nextName,
        color: draftColor,
        icon: draftIcon,
        tags: c.tags,
      },
      vaultKey,
      false,
    );
    setEditing(null);

    const nameChanged = nextName !== editBaselineName;
    const shouldSuggest =
      !iconManuallyPicked && (!draftIcon || nameChanged) && nextName.length > 0;
    if (shouldSuggest) {
      try {
        const res = await api.suggestIcon({
          name: nextName,
          kind: 'customer',
          excludeIcons: exclusionHistory,
        });
        if (res.icon && res.icon !== draftIcon) {
          await upsertCustomer(
            c.id,
            {
              name: nextName,
              color: draftColor,
              icon: res.icon,
              tags: c.tags,
            },
            vaultKey,
            false,
          );
        }
      } catch {
        /* swallow */
      }
    }
  }

  async function removeCustomer(id: string) {
    if (!confirm('Move customer to the Orphanage? All its pages will be hidden but recoverable.')) {
      return;
    }
    await deleteCustomer(id);
  }

  // Compact mode (tablet): narrow icon-only sidebar
  if (compact) {
    return (
      <aside className="w-14 border-r border-ink-800 bg-ink-900 flex flex-col min-h-0">
        <div className="flex items-center justify-center py-2 border-b border-ink-800">
          {canEdit && (
            <button className="btn-ghost !p-1" title="Add customer" onClick={addNew}>
              <Plus size={14} />
            </button>
          )}
        </div>
        <ul className="flex-1 overflow-y-auto">
          {customers.map((c) => {
            const Icon = c.icon ? getAppIcon(c.icon) : null;
            return (
              <li
                key={c.id}
                className={`flex items-center justify-center py-2 cursor-pointer border-l-2 ${
                  selectedId === c.id
                    ? 'bg-ink-800 border-dragon-500'
                    : 'border-transparent hover:bg-ink-800/60'
                }`}
                onClick={() => onSelect(c.id, c.name)}
                title={c.name}
              >
                <span
                  className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                  style={{ backgroundColor: c.color }}
                >
                  {Icon ? <Icon size={14} /> : c.name.charAt(0).toUpperCase()}
                </span>
              </li>
            );
          })}
        </ul>
      </aside>
    );
  }

  // Full mode: on desktop the aside is resizable (auto-fit + drag handle);
  // on mobile it falls back to full-width for the drill-down UI.
  const contentSignature = customers.map((c) => c.name).join('');
  return (
    <ResizableAside
      storageKey="vault.col.customers"
      min={160}
      max={420}
      defaultWidth={240}
      enabled={!compact}
      contentSignature={contentSignature}
      className="border-r border-ink-800 bg-ink-900 flex flex-col min-h-0"
      fallbackClassName="w-full border-r border-ink-800 bg-ink-900 flex flex-col min-h-0"
    >
      {({ measureRef, rowAttr, labelAttr }) => (
        <>
          <div className="flex items-center justify-between px-3 py-2 border-b border-ink-800">
            <span className="text-xs uppercase tracking-wider text-ink-400">Customers</span>
            {canEdit && (
              <button className="btn-ghost !p-1" title="Add customer" onClick={addNew}>
                <Plus size={14} />
              </button>
            )}
          </div>
          <ul
            ref={measureRef as React.Ref<HTMLUListElement>}
            className="flex-1 overflow-y-auto"
          >
        {customers.length === 0 && (
          <li className="p-4 text-xs text-ink-500">No customers yet. Click + to add one.</li>
        )}
        {customers.map((c) => {
          const RowIcon = c.icon ? getAppIcon(c.icon) : null;
          const DraftIcon = draftIcon ? getAppIcon(draftIcon) : null;
          return (
            <li
              key={c.id}
              {...rowAttr}
              className={`group relative px-3 py-1.5 flex items-center gap-2 cursor-pointer border-l-2 ${
                selectedId === c.id
                  ? 'bg-ink-800 border-dragon-500'
                  : 'border-transparent hover:bg-ink-800/60'
              }`}
              onClick={() => onSelect(c.id, c.name)}
            >
              {editing === c.id ? (
                <div
                  className="flex flex-col gap-2 w-full py-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-1.5">
                    <div
                      className="h-7 w-7 flex-shrink-0 flex items-center justify-center rounded border border-ink-700 bg-ink-800"
                      title={draftIcon ?? 'No icon yet — save to auto-pick'}
                    >
                      {DraftIcon ? (
                        <DraftIcon size={14} style={{ color: draftColor }} />
                      ) : (
                        <span
                          className="block h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: draftColor }}
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn-ghost !p-1"
                      title="Regenerate icon (avoid last 5)"
                      onClick={regenerateIcon}
                      disabled={regenerating || !draftName.trim()}
                    >
                      <RefreshCw
                        size={12}
                        className={regenerating ? 'animate-spin' : ''}
                      />
                    </button>
                    <button
                      type="button"
                      className="btn-ghost !p-1"
                      title="Set icon key manually"
                      onClick={() => setShowKeyInput((v) => !v)}
                    >
                      <Pencil size={12} />
                    </button>
                    <input
                      className="input !py-1 !text-sm flex-1 min-w-0"
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(c);
                        if (e.key === 'Escape') setEditing(null);
                      }}
                    />
                  </div>
                  {showKeyInput && (
                    <input
                      className="input !py-1 !text-xs w-full font-mono"
                      value={keyInput}
                      onChange={(e) => setKeyInput(e.target.value)}
                      placeholder="icon key (e.g. shield-check)"
                      onBlur={applyKeyInput}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          applyKeyInput();
                        }
                        if (e.key === 'Escape') {
                          setShowKeyInput(false);
                          setKeyInput('');
                        }
                      }}
                    />
                  )}
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex gap-1 flex-wrap">
                      {PRESET_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setDraftColor(color)}
                          className={`h-5 w-5 rounded-full ${
                            draftColor === color
                              ? 'ring-2 ring-offset-1 ring-offset-ink-900 ring-white'
                              : ''
                          }`}
                          style={{ backgroundColor: color }}
                          aria-label={`Set colour ${color}`}
                        />
                      ))}
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        className="btn-ghost !p-1"
                        onClick={() => setEditing(null)}
                        aria-label="Cancel"
                      >
                        <X size={14} />
                      </button>
                      <button
                        type="button"
                        className="btn-primary !px-2 !py-1"
                        onClick={() => saveEdit(c)}
                        aria-label="Save"
                      >
                        <Check size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {RowIcon ? (
                    <RowIcon
                      size={14}
                      style={{ color: c.color }}
                      className="flex-shrink-0"
                    />
                  ) : (
                    <span
                      className="h-3 w-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: c.color }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div {...labelAttr} className="truncate text-sm">
                      {c.name}
                    </div>
                  </div>
                  <div
                    className={`absolute inset-y-0 right-0 flex items-center gap-1 pl-8 pr-2 transition-opacity bg-gradient-to-l to-transparent ${
                      selectedId === c.id
                        ? 'opacity-100 from-ink-800 via-ink-800'
                        : 'opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto from-ink-800/90 via-ink-800/90'
                    }`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {c.pendingSync && (
                      <span className="text-[10px] text-amber-400 uppercase" title="Pending sync">
                        ●
                      </span>
                    )}
                    {canEdit && (
                      <>
                        <button
                          className="btn-ghost !p-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            beginEdit(c);
                          }}
                          title="Rename"
                        >
                          <Edit2 size={12} />
                        </button>
                        <button
                          className="btn-ghost !p-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeCustomer(c.id);
                          }}
                          title="Delete"
                        >
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </li>
          );
        })}
          </ul>
        </>
      )}
    </ResizableAside>
  );
}
